// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — the PURE voice derivation (principle #23). Everything here is a plain
// function of numbers: a block of PCM samples → a fundamental frequency (Hz) →
// a 0..1 "height" the mote flies at. No Web Audio, no DOM, no time source. The mic
// driver (mic.ts) and the touch/keyboard fallback (input.ts) both produce the SAME
// height, so this module — and the whole game on top of it — never knows nor cares
// which input was used. That is what makes the sensor path unit-testable without a
// real microphone. Nothing is recorded, stored, or sent; only a number leaves here.

export const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);

/** The mapped pitch band: this Hz reads height 0 … this Hz reads height 1 (log scale). */
export const PITCH_LO = 100; // ~G2 — a low, comfortable hum
export const PITCH_HI = 800; // ~G5 — an easy whistle / falsetto

export interface PitchOpts {
  /** Below this RMS the block is treated as silence (no pitch). Default 0.008. */
  rmsGate?: number;
  /** Peak autocorrelation (relative to zero-lag energy) below this = unvoiced. Default 0.5. */
  clarity?: number;
  /** Search range in Hz. */
  minHz?: number;
  maxHz?: number;
}

/** Root-mean-square of a block of PCM samples in [-1, 1]. */
export function rmsOf(samples: Float32Array | number[]): number {
  const n = samples.length;
  if (n === 0) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / n);
}

/**
 * Estimate the fundamental frequency (Hz) of a block of time-domain samples by
 * autocorrelation, with a loudness gate and a clarity gate so a quiet or unvoiced
 * block returns null rather than a garbage pitch. A parabolic interpolation around
 * the best lag gives sub-sample (sub-semitone) resolution. Deterministic and pure.
 */
export function detectPitch(samples: Float32Array | number[], sampleRate: number, opts: PitchOpts = {}): number | null {
  const rmsGate = opts.rmsGate ?? 0.008;
  const clarity = opts.clarity ?? 0.5;
  const minHz = opts.minHz ?? 70;
  const maxHz = opts.maxHz ?? 1100;
  const n = samples.length;
  if (n < 32 || sampleRate <= 0) return null;

  let energy = 0;
  for (let i = 0; i < n; i++) energy += samples[i] * samples[i];
  const rms = Math.sqrt(energy / n);
  if (rms < rmsGate) return null;

  const minLag = Math.max(2, Math.floor(sampleRate / maxHz));
  const maxLag = Math.min(n - 1, Math.floor(sampleRate / minHz));
  if (maxLag <= minLag) return null;

  // Normalised autocorrelation. A running window energy keeps the correlation
  // comparable across lags rather than favouring short lags.
  let gmax = 0;
  const corr = new Float64Array(maxLag + 2);
  for (let lag = minLag; lag <= maxLag; lag++) {
    let c = 0;
    for (let i = 0; i < n - lag; i++) c += samples[i] * samples[i + lag];
    c /= n - lag;
    corr[lag] = c;
    if (c > gmax) gmax = c;
  }
  if (gmax <= 0) return null;

  // Clarity: the peak correlation relative to the zero-lag energy (≈ periodicity).
  if (gmax / (energy / n) < clarity) return null;

  // Pick the FUNDAMENTAL, not an octave-down subharmonic: the FIRST (shortest-lag)
  // local peak that reaches most of the global maximum. A pure tone's autocorrelation
  // peaks at every multiple of the period; taking the global max would pick a longer
  // lag (a lower octave). 0.85·gmax finds the true first period.
  let bestLag = -1;
  for (let lag = minLag + 1; lag < maxLag; lag++) {
    if (corr[lag] >= 0.85 * gmax && corr[lag] >= corr[lag - 1] && corr[lag] >= corr[lag + 1]) {
      bestLag = lag;
      break;
    }
  }
  if (bestLag < 0) {
    // No clear peak — fall back to the global argmax.
    for (let lag = minLag; lag <= maxLag; lag++) if (corr[lag] === gmax) { bestLag = lag; break; }
  }
  if (bestLag < 0) return null;

  // Parabolic interpolation around the peak for sub-sample accuracy.
  let lag = bestLag;
  if (bestLag > minLag && bestLag < maxLag) {
    const a = corr[bestLag - 1];
    const b = corr[bestLag];
    const c = corr[bestLag + 1];
    const denom = a - 2 * b + c;
    if (denom !== 0) lag = bestLag + (0.5 * (a - c)) / denom;
  }
  return sampleRate / lag;
}

/** Map a frequency to a 0..1 height on a log (musical) scale, clamped to the band. */
export function pitchToHeight(hz: number, lo: number = PITCH_LO, hi: number = PITCH_HI): number {
  if (!(hz > 0) || hi <= lo) return 0;
  return clamp((Math.log(hz) - Math.log(lo)) / (Math.log(hi) - Math.log(lo)), 0, 1);
}

/**
 * A little exponential smoother shared by BOTH input paths (the mic and the touch/
 * keyboard fallback), so the mote glides rather than jitters. `to(target, dt)` eases
 * the current value toward a target with time-constant `tauMs`; a null target (a
 * silent block, no finger down) holds the last value. Pure — no clock of its own.
 */
export class HeightSmoother {
  private cur: number;
  constructor(
    initial = 0.5,
    private readonly tauMs = 85,
  ) {
    this.cur = initial;
  }
  to(target: number | null, dtMs: number): number {
    if (target !== null && dtMs > 0) {
      const k = 1 - Math.exp(-dtMs / this.tauMs);
      this.cur += (clamp(target, 0, 1) - this.cur) * k;
    }
    return this.cur;
  }
  set(v: number): void {
    this.cur = clamp(v, 0, 1);
  }
  value(): number {
    return this.cur;
  }
}
